/**
 * Окно подтверждения опасного действия.
 *
 * Одно окно на всю систему, а не подтверждение на месте рядом с кнопкой.
 * Маленькое «Да / ✕» вплотную к корзине опасно: рука уже движется к нему,
 * и удаление происходит вторым щелчком почти без паузы. Тут щелчок попадает
 * в пустое место окна, а не в кнопку, и приходится прочитать, что удаляется.
 *
 * Кнопка подтверждения намеренно НЕ в фокусе: Enter сразу после открытия
 * не должен ничего удалять. Escape и клик по затемнению — отмена.
 */

import { useEffect, type ReactNode } from 'react';
import { AlertTriangle, Check, CheckCircle2, Trash2 } from 'lucide-react';
import { useBodyScrollLock } from './useBodyScrollLock.js';
import { useCloseOnBack } from './useCloseOnBack.js';

export function ConfirmDialog({
  title,
  description,
  details,
  confirmLabel = 'Удалить',
  tone = 'danger',
  onConfirm,
  onCancel,
}: {
  title: string;
  /** Что именно произойдёт — своими словами, без «вы уверены?». */
  description: string;
  /** Дополнительные последствия: что уйдёт вместе с записью. */
  details?: ReactNode;
  confirmLabel?: string;
  /**
   * Что за действие подтверждаем.
   *
   * `danger` — необратимое: удаление записи, отказ от несохранённых правок.
   * Красная кнопка с корзиной и восклицательный знак в кружке.
   *
   * `accent` — обычное, хоть и требующее подтверждения: отметить счёт
   * оплаченным, снять эту отметку. Ничего не пропадает, всё делается обратно.
   *
   * Раньше вид был один на всё, и «Отметить оплаченным» выходило красной
   * кнопкой с корзиной — то есть выглядело как удаление счёта. Цвет и значок
   * в таком окне читаются раньше текста, и читались они неправильно.
   */
  tone?: 'danger' | 'accent';
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const опасное = tone === 'danger';
  const цвет = опасное ? 'var(--otc-coral)' : 'var(--otc-accent)';
  useBodyScrollLock(true);
  useCloseOnBack(true, onCancel);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    /*
     * Клик по затемнению не закрывает: решение об удалении принимается
     * кнопкой, а не промахом мимо окна. Отменить можно кнопкой или Escape.
     */
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4"
      style={{ background: 'rgba(28,27,24,0.55)' }}
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md rounded-2xl p-6 shadow-2xl" style={{ background: 'var(--otc-surface)' }}>
        <div className="mb-4 flex items-start gap-3">
          <span
            className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full"
            style={{ background: `color-mix(in srgb, ${цвет} 14%, transparent)` }}
          >
            {опасное ? (
              <AlertTriangle size={22} style={{ color: цвет }} />
            ) : (
              <CheckCircle2 size={22} style={{ color: цвет }} />
            )}
          </span>
          <div className="min-w-0">
            <h2 className="font-display text-lg font-semibold leading-snug text-ink">{title}</h2>
            <p className="mt-1 text-sm text-ink2">{description}</p>
          </div>
        </div>

        {details && (
          <div
            className="mb-4 rounded-xl px-4 py-3 text-sm"
            style={{ background: 'var(--otc-bg)', color: 'var(--otc-ink)' }}
          >
            {details}
          </div>
        )}

        <div className="flex gap-2">
          <button
            onClick={onConfirm}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium text-white transition-opacity hover:opacity-90"
            style={{ background: цвет }}
          >
            {опасное ? <Trash2 size={15} /> : <Check size={15} />} {confirmLabel}
          </button>
          <button
            onClick={onCancel}
            // Отмена получает фокус: случайный Enter отменяет, а не удаляет.
            autoFocus
            className="rounded-xl border px-5 py-2.5 text-sm font-medium text-ink2 transition-colors hover:bg-black/5"
            style={{ borderColor: 'var(--otc-line)' }}
          >
            Отмена
          </button>
        </div>
      </div>
    </div>
  );
}
